import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import { useTheme } from "@mui/material/styles";
import { useDesign } from "./DesignContext";
import {
  trackMemoCardPageView,
  trackDesignSelected,
} from "./MemoCardAnalytics";

const Design = () => {
  const theme = useTheme();
  const { selectedDesign, setSelectedDesign, designs } = useDesign();

  useEffect(() => {
    trackMemoCardPageView("design");
  }, []);

  const handleDesignSelect = (designId, designTitle) => {
    setSelectedDesign(designId);
    trackDesignSelected(designId, designTitle);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        p: 3,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: `
          radial-gradient(ellipse 80% 60% at 70% 0%, #f3e6fa 60%, transparent 100%),
          linear-gradient(120deg, #f8f6ed 60%, #f3e6fa 100%)
        `,
        backgroundColor: "#f8f6ed",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Floating playful shape for delight */}
      <Box
        sx={{
          position: "absolute",
          top: { xs: -80, sm: -120 },
          left: { xs: -60, sm: -100 },
          width: { xs: 220, sm: 340 },
          height: { xs: 180, sm: 260 },
          background:
            "radial-gradient(circle at 60% 40%, #e9d5ff 60%, transparent 100%)",
          zIndex: 0,
          filter: "blur(8px)",
          opacity: 0.7,
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          maxWidth: "md",
          mx: "auto",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            textAlign: "center",
            mb: 4,
            fontWeight: 600,
          }}
        >
          Pick a card design
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(3, 1fr)",
            },
            gap: 3,
            mb: 4,
          }}
        >
          {designs.map((design) => (
            <Card
              key={design.id}
              onClick={() => handleDesignSelect(design.id, design.title)}
              sx={{
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                transform:
                  selectedDesign === design.id ? "scale(1.05)" : "scale(1)",
                border:
                  selectedDesign === design.id
                    ? `2px solid ${theme.palette.primary.main}`
                    : "none",
                "&:hover": {
                  transform: "scale(1.02)",
                },
              }}
            >
              <CardMedia
                component="img"
                height="140"
                image={design.image}
                alt={design.title}
                sx={{
                  objectFit: "cover",
                }}
              />
              <Box sx={{ p: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    textAlign: "center",
                    fontWeight: 500,
                  }}
                >
                  {design.title}
                </Typography>
              </Box>
            </Card>
          ))}
        </Box>

        <Button
          component={Link}
          to="/memo-card/name"
          variant="contained"
          fullWidth
          disabled={!selectedDesign}
          sx={{
            py: 2,
            fontSize: "1.13rem",
            fontWeight: 700,
            borderRadius: "10px",
            minHeight: "56px",
            background: !selectedDesign
              ? "linear-gradient(90deg, #e2e2e2, #d1d1d1)"
              : "linear-gradient(90deg, #a855f7, #f472b6)",
            color: "white",
            boxShadow: "0 2px 12px 0 rgba(168,85,247,0.10)",
            "&:hover": {
              background: !selectedDesign
                ? "linear-gradient(90deg, #e2e2e2, #d1d1d1)"
                : "linear-gradient(90deg, #b366f8, #f583c0)",
              boxShadow: "0px 0px 16px rgba(168,85,247,0.2)",
            },
            "&:disabled": {
              background: "linear-gradient(90deg, #e2e2e2, #d1d1d1)",
              color: "#9e9e9e",
            },
          }}
        >
          ✨ Continue →
        </Button>
      </Box>
    </Box>
  );
};

export default Design;
